namespace $ {

	export type $shm_hitalama_map_widget_float_table2_query_row = {
		raw: readonly unknown[]
		text: readonly string[]
	}

	export type $shm_hitalama_map_widget_float_table2_query_filter = {
		col: number
		kind: string
		mode: string
		value: string
		until: string
	}

	type Table2_query_column = {
		ready: Set< string >
		filled: number[]
		empty: number[]
		exact: Map< string, number[] >
		trigrams: Map< string, number[] >
		numeric: { value: number, row: number }[]
	}

	export class $shm_hitalama_map_widget_float_table2_query_index {

		readonly all: readonly number[]
		search_trigrams = new Map< string, number[] >
		private search_ready = false
		readonly columns = new Map< number, Table2_query_column >()
		private readonly indexed_columns: ReadonlySet< number >

		constructor(
			readonly rows: readonly $shm_hitalama_map_widget_float_table2_query_row[],
			readonly kinds: readonly string[],
			indexed_columns: readonly number[] = kinds.map( ( _, col )=> col ),
		) {
			this.all = rows.map( ( _, index )=> index )
			this.indexed_columns = new Set( indexed_columns )
		}

		/** Build only for a text search, publishing no partial results on failure. */
		search_index() {
			if( this.search_ready ) return this.search_trigrams
			const next = new Map< string, number[] >
			this.rows.forEach( ( row, row_index )=> {
				const search_grams = new Set< string >
				row.text.forEach( text => {
					for( const gram of this.grams( this.normalize( text ) ) ) search_grams.add( gram )
				} )
				for( const gram of search_grams ) this.post( next, gram, row_index )
			} )
			this.search_trigrams = next
			this.search_ready = true
			return next
		}

		/** Pure cache over this immutable query snapshot, not a persistent DB index. */
		column( col: number, mode = 'all' ) {
			if( !this.indexed_columns.has( col ) ) return null
			const column = this.columns.get( col ) ?? {
				ready: new Set< string >,
				filled: [], empty: [], exact: new Map, trigrams: new Map, numeric: [],
			} as Table2_query_column
			const kind = this.kinds[ col ] ?? 'text'
			const filled = !column.ready.has( 'filled' )
			const exact = ( mode === 'all' || mode === 'equals' ) && !column.ready.has( 'exact' )
			const trigrams = ( mode === 'all' || mode === 'contains' ) && kind === 'text' && !column.ready.has( 'trigrams' )
			const numeric = ( mode === 'all' || [ 'before', 'after', 'range' ].includes( mode ) ) && !column.ready.has( 'numeric' )
			if( !filled && !exact && !trigrams && !numeric ) return column
			// Publish a new part only after its entire scan completes. A failed
			// conversion must not leave partial postings in an existing column.
			const next: Omit< Table2_query_column, 'ready' > = {
				filled: filled ? [] : column.filled, empty: filled ? [] : column.empty,
				exact: exact ? new Map : column.exact,
				trigrams: trigrams ? new Map : column.trigrams,
				numeric: numeric ? [] : column.numeric,
			}
			this.rows.forEach( ( row, row_index )=> {
				const raw = row.raw[ col ]
				const present = raw !== null && raw !== undefined && String( raw ).trim() !== ''
				if( filled ) ( present ? next.filled : next.empty ).push( row_index )
				if( !present ) return
				const parsed = exact || numeric ? this.parse( raw, kind ) : null
				if( exact ) {
					const key = this.key( parsed! )
					if( key !== null ) this.post( next.exact, key, row_index )
				}
				if( trigrams ) {
					for( const gram of new Set( this.grams( this.normalize( raw ) ) ) ) this.post( next.trigrams, gram, row_index )
				}
				if( numeric && typeof parsed === 'number' && Number.isFinite( parsed ) ) next.numeric.push({ value: parsed, row: row_index })
			} )
			if( numeric ) next.numeric.sort( ( left, right )=> left.value - right.value || left.row - right.row )
			Object.assign( column, next )
			for( const [ key, done ] of [ [ 'filled', filled ], [ 'exact', exact ], [ 'trigrams', trigrams ], [ 'numeric', numeric ] ] as const ) {
				if( done ) column.ready.add( key )
			}
			this.columns.set( col, column )
			return column
		}

		normalize( value: unknown ) {
			return String( value ?? '' ).trim().toLocaleLowerCase( 'ru' )
		}

		grams( value: string ) {
			if( value.length < 3 ) return [] as string[]
			const result: string[] = []
			for( let index = 0; index <= value.length - 3; ++ index ) result.push( value.slice( index, index + 3 ) )
			return result
		}

		post( index: Map< string, number[] >, key: string, row: number ) {
			const rows = index.get( key )
			if( rows ) rows.push( row )
			else index.set( key, [ row ] )
		}

		parse( value: unknown, kind: string ): string | number {
			const text = String( value ?? '' ).trim()
			if( kind === 'number' ) return text ? Number( text.replace( /\s/g, '' ).replace( ',', '.' ) ) : NaN
			if( kind !== 'date' ) return text.toLocaleLowerCase( 'ru' )
			const ru = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec( text )
			const iso = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec( text )
			if( !ru && !iso ) return NaN
			const [ y, m, d ] = ru ? [ +ru[3], +ru[2], +ru[1] ] : [ +iso![1], +iso![2], +iso![3] ]
			const date = new Date( Date.UTC( y, m - 1, d ) )
			return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? date.getTime() : NaN
		}

		key( value: string | number ) {
			if( typeof value === 'number' ) return Number.isFinite( value ) ? `n:${ value }` : null
			return `s:${ value }`
		}

		intersect( sources: readonly ( readonly number[] )[] ) {
			if( !sources.length ) return [ ... this.all ]
			const ordered = [ ... sources ].sort( ( left, right )=> left.length - right.length )
			if( !ordered[0].length ) return []
			let result = [ ... ordered[0] ]
			for( let index = 1; index < ordered.length && result.length; ++ index ) {
				const allowed = new Set( ordered[index] )
				result = result.filter( row => allowed.has( row ) )
			}
			return result
		}

		trigram_candidates( index: Map< string, number[] >, needle: string ) {
			const grams = [ ... new Set( this.grams( this.normalize( needle ) ) ) ]
			if( !grams.length ) return [ ... this.all ]
			return this.intersect( grams.map( gram => index.get( gram ) ?? [] ) )
		}

		search_candidates( needle: string ) {
			if( this.normalize( needle ).length < 3 ) return [ ...this.all ]
			return this.trigram_candidates( this.search_index(), needle )
		}

		lower_bound( values: readonly { value: number }[], needle: number ) {
			let left = 0, right = values.length
			while( left < right ) {
				const middle = ( left + right ) >>> 1
				if( values[middle].value < needle ) left = middle + 1
				else right = middle
			}
			return left
		}

		upper_bound( values: readonly { value: number }[], needle: number ) {
			let left = 0, right = values.length
			while( left < right ) {
				const middle = ( left + right ) >>> 1
				if( values[middle].value <= needle ) left = middle + 1
				else right = middle
			}
			return left
		}

		filter_candidates( filter: $shm_hitalama_map_widget_float_table2_query_filter ) {
			if( filter.mode === 'all' ) return [ ... this.all ]
			const column = this.column( filter.col, filter.mode )
			if( !column ) return [ ... this.all ]
			if( filter.mode === 'filled' ) return [ ... column.filled ]
			if( filter.mode === 'empty' ) return [ ... column.empty ]
			if( filter.mode === 'contains' ) return this.trigram_candidates( column.trigrams, filter.value )

			const from = this.parse( filter.value, filter.kind )
			if( filter.mode === 'equals' ) return [ ...( column.exact.get( this.key( from ) ?? '' ) ?? [] ) ]
			if( typeof from !== 'number' || !Number.isFinite( from ) ) return []

			const start = filter.mode === 'before' ? 0 : this.lower_bound( column.numeric, from )
			const until = filter.mode === 'range' ? this.parse( filter.until, filter.kind ) : from
			if( typeof until !== 'number' || !Number.isFinite( until ) ) return []
			const end = filter.mode === 'after' ? column.numeric.length : this.upper_bound( column.numeric, until )
			return column.numeric.slice( start, end ).map( item => item.row ).sort( ( left, right )=> left - right )
		}

	}

}
